import { StcsFstmtEnum } from '~/protos/gen/stcs_enums.entity'
import { DecimalPlacesEnum, DecimalSeparatorEnum, NegativeNumberFormatEnum, NumberFormatEnum, type NumberFormatOptions, PercentageDecimalPlaces } from '~/protos/gen/stcs_user.entity'
import type { StcsWappSettings, StcsWappSettingsCompConfig, StcsWappSettingsConfig } from '~/protos/gen/stcs_wapp_settings.entity'

export interface DateRange {
  start_date: string
  end_date: string
}

export interface DateRangeStrategy {
  strategy_id: string
  custom_start_date?: string
  custom_end_date?: string
}

export interface LevelsFilter {
  subTotals: boolean
  level1: boolean
  level2: boolean
  tags: boolean
  accounts: boolean
}

export interface HideEmptyRowsFilter {
  subTotals: boolean
  level1: boolean
  level2: boolean
  tags: boolean
  accounts: boolean
  otherCashFlow?: boolean
}

export interface FinancialStatementSettings {
  levels: LevelsFilter
  hide_empty: HideEmptyRowsFilter
  show_growth_percent: boolean
}

export interface CompanyFinancialSettings {
  _placeholder?: never
}

export interface CompanyAnalysisSettings {
  show_growth_percent: boolean
}

export interface KpiListItem {
  id: string
  iskpis: boolean
}

export interface InPerOfReference {
  referenceRowId: string
  mapKey: 'pnl' | 'bs' | 'cf'
}

export interface CompanyDashboardSettings {
  top_cards: string[]
  top_cards_margin_refs?: string[]
  top_cards_margin_abs?: boolean[]
  kpi_list: KpiListItem[]
  financial_in_per_of?: string
}

export interface UserFinancialSettings {
  pnl: FinancialStatementSettings
  bs: FinancialStatementSettings
  cf: FinancialStatementSettings
  selected_plan_id?: string
  comparison_date_range?: DateRangeStrategy
  hide_old_years: boolean
  last_actuals?: string
  hide_old_months: boolean
  /** 'in % of' reference for PNL comparison view */
  financial_in_per_of_pnl?: InPerOfReference
  /** 'in % of' reference for Balance Sheet comparison view */
  financial_in_per_of_bs?: InPerOfReference
  /** 'in % of' reference for Cash Flow comparison view */
  financial_in_per_of_cf?: InPerOfReference
}

export interface UserAnalysisSettings {
  selected_plan_id?: string
  last_actuals?: string
}

export interface UserDashboardSettings {
  selected_plan_id?: string
  last_actuals?: string
}

export interface PlanSpecificSettings {
  selected_ctx_row?: Record<string, { reference_row_id: string; calculation_type: 'growth' | 'percentage' }>
  selected_seasonality?: string
}

export interface UserPlanningSettings {
  selected_plan_id?: string
  hide_old_years: boolean
  show_status_column?: boolean
  ref_plans_map?: Record<string, PlanSpecificSettings>
  show_growth_percent: boolean
  pnl_levels?: LevelsFilter
  bs_levels?: LevelsFilter
  cf_levels?: LevelsFilter
}

export interface ParsedWappSettings {
  id: string
  version: string
  meta: {
    ref_id_org: string
    obj_updated_iso_utc: string
    obj_created_iso_utc: string
  }
  org: ParsedWappSettingsConfig
  comp: { [key: string]: ParsedWappSettingsCompConfig }
}

export interface ParsedWappSettingsConfig {
  sub_section_map: {
    financials?: CompanyFinancialSettings
    analysis?: CompanyAnalysisSettings
    dashboard?: CompanyDashboardSettings
    cmw?: string
    // User-specific settings stored with pattern: user_settings_{userId}
    [key: string]: CompanyFinancialSettings | CompanyAnalysisSettings | CompanyDashboardSettings | UserOrgSpecificSettings | string | undefined
  }
}

// User-specific settings at the org level (per user, per org)
export interface UserOrgSpecificSettings {
  number_format?: NumberFormatOptions
}

export interface ParsedWappUserSettingsConfig {
  sub_section_map: {
    financials?: UserFinancialSettings
    analysis?: UserAnalysisSettings
    dashboard?: UserDashboardSettings
    plan?: UserPlanningSettings
    cmw?: string
    [key: string]: UserFinancialSettings | UserAnalysisSettings | UserDashboardSettings | UserPlanningSettings | string | undefined
  }
}

export interface ParsedWappSettingsCompConfig {
  sub_section_map: {
    financials?: CompanyFinancialSettings
    analysis?: CompanyAnalysisSettings
    dashboard?: CompanyDashboardSettings
    cmw?: string
    [key: string]: CompanyFinancialSettings | CompanyAnalysisSettings | CompanyDashboardSettings | string | undefined
  }
  user: { [userId: string]: ParsedWappUserSettingsConfig }
}

const createDefaultFinancialStatementSettings = (fstmt: StcsFstmtEnum): FinancialStatementSettings => ({
  levels: {
    // P&L does not have subtotals; BS defaults to false, CF defaults to true
    subTotals: fstmt === StcsFstmtEnum.SFSE_CF,
    level1: true,
    level2: true,
    tags: false,
    accounts: true,
  },
  hide_empty: {
    // P&L has no subtotals to hide; BS and CF have subtotals
    subTotals: fstmt !== StcsFstmtEnum.SFSE_PNL,
    level1: true,
    level2: true,
    tags: true,
    accounts: true,
  },
  show_growth_percent: true,
})

export const default_user_financial_settings: UserFinancialSettings = {
  pnl: createDefaultFinancialStatementSettings(StcsFstmtEnum.SFSE_PNL),
  bs: createDefaultFinancialStatementSettings(StcsFstmtEnum.SFSE_BS),
  cf: createDefaultFinancialStatementSettings(StcsFstmtEnum.SFSE_CF),
  selected_plan_id: undefined,
  comparison_date_range: undefined,
  hide_old_years: false,
  hide_old_months: false,
}

export const default_user_analysis_settings: UserAnalysisSettings = {
  selected_plan_id: undefined,
}

export const default_user_dashboard_settings: UserDashboardSettings = {
  selected_plan_id: undefined,
}

export const default_user_planning_settings: UserPlanningSettings = {
  selected_plan_id: undefined,
  hide_old_years: false,
  show_status_column: true,
  show_growth_percent: true,
  ref_plans_map: {},
  // PL and BS: Default to Level 2 only (subtotals, level1, level2 unchecked, accounts and tags unchecked)
  pnl_levels: { level1: false, level2: true, tags: false, accounts: false, subTotals: false },
  bs_levels: { level1: false, level2: true, tags: false, accounts: false, subTotals: false },
  // CF: Default to Subtotals + Level 1 + Level 2 checked (Account groups and Accounts unchecked)
  cf_levels: { subTotals: true, level1: true, level2: true, tags: false, accounts: false },
}

export const default_user_number_format_settings: NumberFormatOptions = {
  number_format: NumberFormatEnum.NFO_TAUSEND, // Display in thousands
  decimal_places: DecimalPlacesEnum.D_ZERO, // 0 decimals for numbers
  decimal_separator: DecimalSeparatorEnum.DS_POINT, // Point (.)
  negative_number_format: NegativeNumberFormatEnum.NNF_BRACKETS, // Brackets: -152 = (152)
  percentage_decimal_places: PercentageDecimalPlaces.PDP_ONE, // 1 decimal for percentages
}

export const default_company_dashboard_settings: CompanyDashboardSettings = {
  top_cards: ['level_1_revenues', 'total_contribution_margin_1', 'total_ebitda', 'total_cash_n_cash_equivalents'],
  top_cards_margin_refs: ['', 'total_output', 'total_output', ''],
  top_cards_margin_abs: [false, false, false, false],
  financial_in_per_of: 'total_output',
  kpi_list: [
    { id: 'level_1_cash_n_cash_equivalents', iskpis: false },
    { id: 'kpi_nwc_dso', iskpis: true },
    { id: 'kpi_nwc_dio', iskpis: true },
    { id: 'kpi_nwc_dpo', iskpis: true },
    { id: 'kpi_debt_net_debt', iskpis: true },
    { id: 'kpi_debt_net_debt_ebitda', iskpis: true },
    { id: 'kpi_debt_d_e', iskpis: true },
  ],
}

const safeJsonParse = (value: string | unknown, fallback: unknown = null): unknown => {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch {
    return fallback || value
  }
}

const transformSubSectionMap = (subSectionMap: Record<string, string> | undefined): Record<string, unknown> => {
  if (!subSectionMap) return {}

  return Object.entries(subSectionMap).reduce(
    (accumulator, [settingKey, settingValue]) => {
      accumulator[settingKey] = settingKey === 'cmw' ? settingValue : safeJsonParse(settingValue)
      return accumulator
    },
    {} as Record<string, unknown>,
  )
}

const stringifySubSectionMap = (subSectionMap: Record<string, unknown> | undefined): Record<string, string> => {
  if (!subSectionMap) return {}

  return Object.entries(subSectionMap).reduce(
    (accumulator, [settingKey, settingValue]) => {
      accumulator[settingKey] = settingKey === 'cmw' || typeof settingValue === 'string' ? (settingValue as string) : JSON.stringify(settingValue)
      return accumulator
    },
    {} as Record<string, string>,
  )
}

export const parseWappSettingsConfig = (configuration: StcsWappSettingsConfig): ParsedWappSettingsConfig => ({
  sub_section_map: transformSubSectionMap(configuration.sub_section_map) as ParsedWappSettingsConfig['sub_section_map'],
})

export const parseWappUserSettingsConfig = (configuration: StcsWappSettingsConfig): ParsedWappUserSettingsConfig => ({
  sub_section_map: transformSubSectionMap(configuration.sub_section_map) as ParsedWappUserSettingsConfig['sub_section_map'],
})

export const parseWappSettingsCompConfig = (configuration: StcsWappSettingsCompConfig): ParsedWappSettingsCompConfig => ({
  sub_section_map: transformSubSectionMap(configuration.sub_section_map) as ParsedWappSettingsCompConfig['sub_section_map'],
  user: Object.entries(configuration.user || {}).reduce(
    (accumulator, [userIdentifier, userConfiguration]) => {
      accumulator[userIdentifier] = parseWappUserSettingsConfig(userConfiguration)
      return accumulator
    },
    {} as { [userId: string]: ParsedWappUserSettingsConfig },
  ),
})

export const stringifyWappSettingsConfig = (parsed: ParsedWappSettingsConfig): StcsWappSettingsConfig => ({
  sub_section_map: stringifySubSectionMap(parsed.sub_section_map),
})

export const stringifyWappUserSettingsConfig = (parsed: ParsedWappUserSettingsConfig): StcsWappSettingsConfig => ({
  sub_section_map: stringifySubSectionMap(parsed.sub_section_map),
})

export const stringifyWappSettingsCompConfig = (parsed: ParsedWappSettingsCompConfig): StcsWappSettingsCompConfig => ({
  sub_section_map: stringifySubSectionMap(parsed.sub_section_map),
  user: Object.entries(parsed.user).reduce(
    (accumulator, [userIdentifier, userConfiguration]) => {
      accumulator[userIdentifier] = stringifyWappUserSettingsConfig(userConfiguration)
      return accumulator
    },
    {} as { [userId: string]: StcsWappSettingsConfig },
  ),
})

export const parseWappSettings = (apiSettings: StcsWappSettings): ParsedWappSettings => ({
  id: apiSettings.id || '',
  version: apiSettings.version || '',
  meta: {
    ref_id_org: apiSettings.meta?.ref_id_org || '',
    obj_updated_iso_utc: apiSettings.meta?.obj_updated_iso_utc || '',
    obj_created_iso_utc: apiSettings.meta?.obj_created_iso_utc || '',
  },
  org: parseWappSettingsConfig(apiSettings.org || { sub_section_map: {} }),
  comp: Object.entries(apiSettings.comp || {}).reduce(
    (accumulator, [companyIdentifier, configuration]) => {
      accumulator[companyIdentifier] = parseWappSettingsCompConfig(configuration)
      return accumulator
    },
    {} as { [key: string]: ParsedWappSettingsCompConfig },
  ),
})

export const stringifyWappSettings = (parsedSettings: ParsedWappSettings): StcsWappSettings => ({
  id: parsedSettings.id,
  version: parsedSettings.version,
  meta: {
    ref_id_org: parsedSettings.meta.ref_id_org,
    obj_updated_iso_utc: parsedSettings.meta.obj_updated_iso_utc,
    obj_created_iso_utc: parsedSettings.meta.obj_created_iso_utc,
  },
  org: stringifyWappSettingsConfig(parsedSettings.org),
  comp: Object.entries(parsedSettings.comp).reduce(
    (accumulator, [companyIdentifier, configuration]) => {
      accumulator[companyIdentifier] = stringifyWappSettingsCompConfig(configuration)
      return accumulator
    },
    {} as { [key: string]: StcsWappSettingsCompConfig },
  ),
})
